import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import {
  BarChart2, Truck, Calendar, XCircle,
  DollarSign, Activity, Info, FileDown, FileText,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';
import ExportMenu from '../components/ExportMenu';
import { STATUS_LABELS, downloadTablePDF } from '../utils/exportHelpers';

// Column sets shared by the report exports (screen table and detailed lines)
const money = (v) => parseFloat(v || 0).toFixed(2);
const dateOnly = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '');
const INVOICE_LINES = [
  { header: 'Invoice', value: (r) => r.invoice_number, width: 14 },
  { header: 'Date', value: (r) => dateOnly(r.date), width: 12 },
  { header: 'Client', value: (r) => r.client, width: 32 },
  { header: 'Excl VAT', value: (r) => money(r.subtotal), align: 'right', width: 12 },
  { header: 'VAT', value: (r) => money(r.vat_amount), align: 'right', width: 10 },
  { header: 'Incl VAT', value: (r) => money(r.total), align: 'right', width: 12 },
  { header: 'Status', value: (r) => r.status, width: 10 },
];
const CREDIT_LINES = [
  { header: 'Credit note', value: (r) => r.credit_note_number, width: 16 },
  { header: 'Date', value: (r) => dateOnly(r.date), width: 12 },
  { header: 'Client', value: (r) => r.client, width: 32 },
  { header: 'Against invoice', value: (r) => r.invoice_number, width: 14 },
  { header: 'Excl VAT', value: (r) => money(r.subtotal), align: 'right', width: 12 },
  { header: 'VAT', value: (r) => money(r.vat_amount), align: 'right', width: 10 },
  { header: 'Total', value: (r) => money(r.total), align: 'right', width: 12 },
];
const sumOf = (rows, k) => rows.reduce((s, r) => s + parseFloat(r[k] || 0), 0);

// ── Overview ──────────────────────────────────────────────────────────────────
function TabOverview() {
  const { data: daily = [] } = useQuery({
    queryKey: ['daily-report'],
    queryFn: () => api.get('/reports/daily').then(r => r.data),
  });

  const chartData = [...daily].reverse().map(d => ({
    date:      new Date(d.date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }),
    Delivered: parseInt(d.delivered),
    Failed:    parseInt(d.failed),
    Total:     parseInt(d.total),
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h2 className="font-semibold text-gray-700 mb-4">Daily Volume — Last 30 Days</h2>
      {chartData.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-8">No data yet</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Total" fill="#e5e7eb" />
            <Bar dataKey="Delivered" fill="#22c55e" />
            <Bar dataKey="Failed" fill="#ef4444" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Drivers ───────────────────────────────────────────────────────────────────
function TabDrivers() {
  const today        = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to,   setTo]   = useState(today);

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['driver-stats', from, to],
    queryFn: () => api.get('/reports/drivers', { params: { from, to } }).then(r => r.data),
  });
  const { data: allDrivers = [] } = useQuery({
    queryKey: ['all-drivers'],
    queryFn: () => api.get('/users/drivers').then(r => r.data),
  });

  const statsMap = Object.fromEntries(drivers.map(d => [d.driver_id, d]));

  const quickRange = (days) => {
    setTo(new Date().toISOString().split('T')[0]);
    setFrom(new Date(Date.now() - days * 86400000).toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar size={15} className="text-gray-400" />
            <span className="font-medium">Period:</span>
          </div>
          {[['7d', 7], ['30d', 30], ['90d', 90]].map(([lbl, days]) => (
            <button key={lbl} onClick={() => quickRange(days)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                from === new Date(Date.now() - days * 86400000).toISOString().split('T')[0] && to === today
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'border-gray-300 text-gray-600 hover:border-orange-400'
              }`}>
              Last {lbl}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {allDrivers.map(driver => {
              const s = statsMap[driver.id] || { collected: 0, delivered: 0, failed: 0 };
              const total = parseInt(s.delivered) + parseInt(s.failed);
              const rate  = total > 0 ? Math.round((parseInt(s.delivered) / total) * 100) : null;
              return (
                <div key={driver.id} className="bg-white rounded-xl shadow-sm p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-800">{driver.name}</p>
                      <p className="text-xs text-gray-400">{driver.phone || 'No phone'}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${driver.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {driver.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <p className="text-lg font-bold text-blue-600">{s.collected || 0}</p>
                      <p className="text-xs text-blue-400">Collected</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <p className="text-lg font-bold text-green-600">{s.delivered || 0}</p>
                      <p className="text-xs text-green-400">Delivered</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-lg font-bold text-red-600">{s.failed || 0}</p>
                      <p className="text-xs text-red-400">Failed</p>
                    </div>
                  </div>
                  {rate !== null ? (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Success rate</span>
                        <span className={`font-semibold ${rate >= 90 ? 'text-green-600' : rate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {rate}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${rate}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 text-center">No deliveries in this period</p>
                  )}
                </div>
              );
            })}
          </div>
          {allDrivers.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">No drivers added yet</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Cancelled ─────────────────────────────────────────────────────────────────
function TabCancelled() {
  const today        = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to,   setTo]   = useState(today);

  const { data: cancelled = [], isLoading } = useQuery({
    queryKey: ['cancelled-report', from, to],
    queryFn: () => api.get('/reports/cancelled', { params: { from, to } }).then(r => r.data),
  });

  const quickRange = (days) => {
    setTo(new Date().toISOString().split('T')[0]);
    setFrom(new Date(Date.now() - days * 86400000).toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar size={15} className="text-gray-400" />
            <span className="font-medium">Period:</span>
          </div>
          {[['7d', 7], ['30d', 30], ['90d', 90]].map(([lbl, days]) => (
            <button key={lbl} onClick={() => quickRange(days)}
              className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:border-orange-400 transition-colors">
              Last {lbl}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <XCircle size={22} className="text-red-600" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-800">{cancelled.length}</p>
          <p className="text-sm text-gray-500">Cancelled parcels in this period</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : cancelled.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No cancelled parcels in this period</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Tracking</th>
                  <th className="px-4 py-3 text-left">Recipient</th>
                  <th className="px-4 py-3 text-left">Sender</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Cancelled</th>
                  <th className="px-4 py-3 text-left">Cancelled by</th>
                  <th className="px-4 py-3 text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cancelled.map(p => (
                  <tr key={p.tracking_number} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-800">
                      <a href={`/parcels/${p.tracking_number}`} className="hover:text-orange-500">
                        {p.tracking_number}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{p.recipient_name || '—'}</p>
                      {p.recipient_phone && <p className="text-xs text-gray-400">{p.recipient_phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.sender_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(p.created_at).toLocaleDateString('en-ZA')}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {p.cancelled_at
                        ? new Date(p.cancelled_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{p.cancelled_by || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-700 text-xs font-medium">
                      {p.final_price ? `R${parseFloat(p.final_price).toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Client Report ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  created:          'bg-gray-100 text-gray-700',
  collected:        'bg-blue-100 text-blue-700',
  at_depot:         'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered:        'bg-green-100 text-green-700',
  failed_delivery:  'bg-red-100 text-red-700',
  returned_to_depot:'bg-orange-100 text-orange-700',
  rescheduled:      'bg-indigo-100 text-indigo-700',
  cancelled:        'bg-red-200 text-red-900',
};

// ── Shared bits for the new reports ───────────────────────────────────────────
const Rand = v => `R${parseFloat(v || 0).toFixed(2)}`;
const dmy  = d => new Date(d).toLocaleDateString('en-ZA');
const hm   = d => d ? new Date(d).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' }) : '—';

const FAILURE_LABELS = {
  client_not_home: 'Client not at home',
  out_of_time:     'Out of time',
  vehicle_breakdown:'Vehicle breakdown',
  wrong_address:   'Wrong address',
  refused:         'Refused delivery',
  other:           'Other',
};

// Plain-language explainer box shown at the top of every report
function Explain({ children }) {
  return (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
      <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-blue-900">{children}</p>
    </div>
  );
}

// Shared period picker (presets, month stepping, custom dates) — see
// components/DateRangeBar. Aliased so every report here picks it up.
const RangeBar = DateRangeBar;

function StatCard({ label, value, sub, colorClass = 'bg-gray-50 text-gray-700' }) {
  return (
    <div className={`rounded-xl p-4 ${colorClass}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs mt-0.5 opacity-75">{label}</p>
      {sub && <p className="text-[11px] mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

function useRange(days = 30) {
  const today = new Date().toISOString().split('T')[0];
  const start = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  const [from, setFrom] = useState(start);
  const [to,   setTo]   = useState(today);
  return { from, to, setFrom, setTo };
}

// ── 1. Revenue ────────────────────────────────────────────────────────────────
export function TabRevenue() {
  const { from, to, setFrom, setTo } = useRange(30);
  const rangeDays = (new Date(to) - new Date(from)) / 86400000;
  const group = rangeDays > 62 ? 'month' : 'day';

  const { data, isLoading } = useQuery({
    queryKey: ['revenue', from, to, group],
    queryFn: () => api.get('/reports/revenue', { params: { from, to, group } }).then(r => r.data),
  });

  const t = data?.totals;
  const avg = t && parseInt(t.billed) > 0 ? parseFloat(t.revenue) / parseInt(t.billed) : 0;
  const chart = (data?.series || []).map(s => ({
    period: group === 'month'
      ? new Date(s.period).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' })
      : new Date(s.period).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }),
    'Revenue (R)': Math.round(parseFloat(s.revenue)),
  }));

  return (
    <div className="space-y-4">
      <Explain>
        <strong>How much money your parcels earned.</strong> Every parcel created in the period counts
        (cancelled ones are left out). A parcel is "billed" when it has a price — either typed in manually
        or from a price tier. If Billed is lower than Parcels, some parcels have no price — see the
        <strong> Unbilled</strong> report.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div> : t && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total revenue"  value={Rand(t.revenue)} colorClass="bg-green-50 text-green-700" />
            <StatCard label="Parcels created" value={t.parcels} colorClass="bg-blue-50 text-blue-700" />
            <StatCard label="Parcels with a price (billed)" value={t.billed}
              sub={parseInt(t.parcels) > parseInt(t.billed) ? `${parseInt(t.parcels) - parseInt(t.billed)} have NO price` : 'All parcels have a price ✓'}
              colorClass={parseInt(t.parcels) > parseInt(t.billed) ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'} />
            <StatCard label="Average price per parcel" value={Rand(avg)} colorClass="bg-orange-50 text-orange-700" />
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="font-semibold text-gray-700 mb-1">Revenue per {group === 'month' ? 'month' : 'day'}</h2>
            <p className="text-xs text-gray-400 mb-4">
              Manual prices: {Rand(t.manual_revenue)} · Price-tier prices: {Rand(t.tier_revenue)}
            </p>
            {chart.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">No parcels in this period</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chart}>
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => `R${v}`} />
                  <Bar dataKey="Revenue (R)" fill="#f97316" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── 3. Top clients ────────────────────────────────────────────────────────────
// What you declare on a VAT201. Output VAT = VAT on invoices issued MINUS VAT
// on credit notes issued, because a credit note reduces what SARS is owed.
export function TabVat() {
  // VAT periods are two months, so default to the last full two-month stretch
  const defFrom = () => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  };
  const defTo = () => {
    const d = new Date(); d.setDate(0);   // last day of last month
    return d.toISOString().split('T')[0];
  };
  const [from, setFrom] = useState(defFrom());
  const [to, setTo] = useState(defTo());
  const [hist, setHist] = useState(false);
  const [detail, setDetail] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vat-report', from, to, hist],
    queryFn: () => api.get('/billing/vat-report', { params: { from, to, include_history: hist || undefined } }).then((r) => r.data),
    enabled: !!from && !!to,
    retry: false,
  });

  // Line-by-line backing detail — every invoice and credit note in the period
  const { data: lines, isLoading: linesLoading } = useQuery({
    queryKey: ['vat-detail', from, to, hist],
    queryFn: () => api.get('/billing/vat-report/detail', { params: { from, to, include_history: hist || undefined } }).then((r) => r.data),
    enabled: detail && !!from && !!to,
    retry: false,
  });

  const R = (v) => `R ${parseFloat(v || 0).toFixed(2)}`;

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['VAT report', `${data.from} to ${data.to}`],
      ['Basis', 'Invoice (VAT is declared in the period the invoice was issued)'],
      ['VAT rate', `${data.vat_rate}%`],
      [],
      ['', 'Count', 'Excl VAT', 'VAT', 'Incl VAT'],
      ['Invoices issued', data.invoices.count, data.invoices.subtotal, data.invoices.vat, data.invoices.total],
      ['Less credit notes', data.credit_notes.count, data.credit_notes.subtotal, data.credit_notes.vat, data.credit_notes.total],
      ['OUTPUT VAT PAYABLE', '', data.net.subtotal, data.net.vat, data.net.total],
      [],
      ['Month', 'Invoices', 'Credit notes', 'Excl VAT', 'VAT', 'Incl VAT'],
      ...data.monthly.map((m) => [m.month, m.invoice_count, m.credit_note_count, m.subtotal, m.vat, m.total]),
    ];
    const csv = rows.map((r) => r.map((c) => (/[",\n]/.test(String(c)) ? `"${c}"` : c)).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `VAT-report-${data.from}-to-${data.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Fetch the line detail on demand, so Excel/PDF can include every document
  // even when the detail table isn't open on screen.
  const getLines = async () => {
    if (lines) return lines;
    const r = await api.get('/billing/vat-report/detail', {
      params: { from, to, include_history: hist || undefined },
    });
    return r.data;
  };

  const exportExcel = async () => {
    if (!data) return;
    const d = await getLines();
    const summary = [
      ['VAT report', `${data.from} to ${data.to}`],
      ['Basis', 'Invoice — VAT is declared in the period the invoice was issued'],
      ['VAT rate', `${data.vat_rate}%`],
      ['QuickBooks history', hist ? 'INCLUDED' : 'excluded'],
      [],
      ['', 'Count', 'Excl VAT', 'VAT', 'Incl VAT'],
      ['Invoices issued', data.invoices.count, data.invoices.subtotal, data.invoices.vat, data.invoices.total],
      ['Less credit notes', data.credit_notes.count, -data.credit_notes.subtotal, -data.credit_notes.vat, -data.credit_notes.total],
      ['OUTPUT VAT PAYABLE', '', data.net.subtotal, data.net.vat, data.net.total],
      [],
      ['Month', 'Invoices', 'Credit notes', 'Excl VAT', 'VAT', 'Incl VAT'],
      ...data.monthly.map((m) => [m.month, m.invoice_count, m.credit_note_count, m.subtotal, m.vat, m.total]),
    ];
    const detailRows = [
      ['Date', 'Type', 'Number', 'Client', 'Client VAT no', 'Source', 'Excl VAT', 'VAT', 'Incl VAT'],
      ...(d?.lines || []).map((l) => [
        l.date, l.type, l.number, l.client, l.client_vat || '',
        l.source === 'quickbooks' ? `QuickBooks ${l.source_ref || ''}`.trim() : 'Expressway',
        l.subtotal, l.vat, l.total,
      ]),
      [],
      ['TOTAL', '', '', '', '', '', d?.totals?.subtotal ?? 0, d?.totals?.vat ?? 0, d?.totals?.total ?? 0],
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'VAT201 Summary');
    const ws = XLSX.utils.aoa_to_sheet(detailRows);
    ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 30 }, { wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Detail');
    XLSX.writeFile(wb, `VAT-report-${data.from}-to-${data.to}.xlsx`);
  };

  const exportPdf = async () => {
    if (!data) return;
    const d = await getLines();
    const money = (v) => parseFloat(v || 0).toFixed(2);
    downloadTablePDF(
      d?.lines || [],
      [
        { header: 'Date',     value: (l) => l.date, pdfWidth: 22 },
        { header: 'Type',     value: (l) => l.type, pdfWidth: 24 },
        { header: 'Number',   value: (l) => l.number, pdfWidth: 28 },
        { header: 'Client',   value: (l) => l.client },
        { header: 'VAT no',   value: (l) => l.client_vat || '', pdfWidth: 26 },
        { header: 'Source',   value: (l) => (l.source === 'quickbooks' ? 'QuickBooks' : 'Expressway'), pdfWidth: 24 },
        { header: 'Excl VAT', value: (l) => money(l.subtotal), align: 'right', pdfWidth: 24 },
        { header: 'VAT',      value: (l) => money(l.vat), align: 'right', pdfWidth: 22 },
        { header: 'Incl VAT', value: (l) => money(l.total), align: 'right', pdfWidth: 24 },
      ],
      `VAT-report-${data.from}-to-${data.to}.pdf`,
      `VAT201 — output VAT R${money(data.net.vat)} (excl VAT sales R${money(data.net.subtotal)})`,
      `${data.from} to ${data.to} · invoice basis · ${data.vat_rate}% VAT${hist ? ' · includes QuickBooks history' : ''}`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          <strong>Output VAT for your VAT201.</strong> This is the VAT on the invoices you issued in the
          period, less the VAT on any credit notes — a credit note gives VAT back, so it reduces what you
          owe SARS. An invoice counts in the period it was <strong>issued</strong>, not when it was paid.
        </p>
      </div>

      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <HistoryToggle on={hist} setOn={setHist} />
      <div className="flex flex-wrap justify-end gap-2">
        <button onClick={() => setDetail((d) => !d)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${
            detail ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-700 hover:border-orange-400'
          }`}>
          <FileText size={15} /> {detail ? 'Hide detail' : 'Detailed VAT report'}
        </button>
        <button onClick={exportCsv} disabled={!data}
          className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40">
          <FileDown size={15} /> CSV
        </button>
        <button onClick={exportExcel} disabled={!data}
          className="flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40">
          <FileDown size={15} /> Excel
        </button>
        <button onClick={exportPdf} disabled={!data}
          className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40">
          <FileDown size={15} /> PDF
        </button>
      </div>

      {error ? (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center text-sm text-red-600">
          {error.response?.data?.error || 'Could not build the VAT report'}
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      ) : data && (
        <>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left"></th>
                  <th className="px-4 py-2 text-right">Count</th>
                  <th className="px-4 py-2 text-right">Excl VAT</th>
                  <th className="px-4 py-2 text-right">VAT</th>
                  <th className="px-4 py-2 text-right">Incl VAT</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-4 py-3 text-gray-700">Invoices issued</td>
                  <td className="px-4 py-3 text-right text-gray-500">{data.invoices.count}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{R(data.invoices.subtotal)}</td>
                  <td className="px-4 py-3 text-right text-gray-800 font-medium">{R(data.invoices.vat)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{R(data.invoices.total)}</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">Less credit notes</td>
                  <td className="px-4 py-3 text-right text-gray-500">{data.credit_notes.count}</td>
                  <td className="px-4 py-3 text-right text-purple-700">− {R(data.credit_notes.subtotal)}</td>
                  <td className="px-4 py-3 text-right text-purple-700 font-medium">− {R(data.credit_notes.vat)}</td>
                  <td className="px-4 py-3 text-right text-purple-700">− {R(data.credit_notes.total)}</td>
                </tr>
                <tr className="bg-orange-50 font-bold">
                  <td className="px-4 py-3 text-orange-900">Output VAT payable</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right text-orange-900">{R(data.net.subtotal)}</td>
                  <td className="px-4 py-3 text-right text-orange-900 text-base">{R(data.net.vat)}</td>
                  <td className="px-4 py-3 text-right text-orange-900">{R(data.net.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Month by month</h3>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Month</th>
                  <th className="px-4 py-2 text-right">Invoices</th>
                  <th className="px-4 py-2 text-right">Credit notes</th>
                  <th className="px-4 py-2 text-right">Excl VAT</th>
                  <th className="px-4 py-2 text-right">VAT</th>
                  <th className="px-4 py-2 text-right">Incl VAT</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.monthly.map((m) => (
                  <tr key={m.month} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-700">
                      {new Date(`${m.month}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">{m.invoice_count}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{m.credit_note_count || '—'}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{R(m.subtotal)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-800">{R(m.vat)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{R(m.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Every document behind the totals — answers a SARS query line by line */}
          {detail && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">
                Detailed VAT report — every document in the period
              </h3>
              {linesLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
              : !lines?.lines?.length ? <div className="p-8 text-center text-gray-400">No documents in this period</div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Number</th>
                        <th className="px-3 py-2 text-left">Client</th>
                        <th className="px-3 py-2 text-left">VAT no</th>
                        <th className="px-3 py-2 text-right">Excl VAT</th>
                        <th className="px-3 py-2 text-right">VAT</th>
                        <th className="px-3 py-2 text-right">Incl VAT</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {lines.lines.map((l, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{l.date}</td>
                          <td className="px-3 py-2 text-xs">
                            <span className={l.type === 'Credit Note' ? 'text-purple-700' : 'text-gray-700'}>{l.type}</span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">
                            {l.number}
                            {l.source === 'quickbooks' && (
                              <span className="ml-1.5 text-[10px] bg-gray-100 text-gray-500 rounded px-1 py-0.5">QuickBooks</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">{l.client}</td>
                          <td className="px-3 py-2 text-xs text-gray-400 font-mono">{l.client_vat || '—'}</td>
                          <td className="px-3 py-2 text-right text-xs text-gray-600">{R(l.subtotal)}</td>
                          <td className="px-3 py-2 text-right text-xs font-medium text-gray-800">{R(l.vat)}</td>
                          <td className="px-3 py-2 text-right text-xs text-gray-600">{R(l.total)}</td>
                        </tr>
                      ))}
                      <tr className="bg-orange-50 font-bold">
                        <td className="px-3 py-2 text-orange-900 text-xs" colSpan={5}>
                          Total — {lines.totals.count} documents
                        </td>
                        <td className="px-3 py-2 text-right text-orange-900 text-xs">{R(lines.totals.subtotal)}</td>
                        <td className="px-3 py-2 text-right text-orange-900">{R(lines.totals.vat)}</td>
                        <td className="px-3 py-2 text-right text-orange-900 text-xs">{R(lines.totals.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 px-1">
            Prices include {data.vat_rate}% VAT, carved out at invoice level. Cancelled invoices are excluded.
            Check these figures against your records before submitting a return.
          </p>
        </>
      )}
    </div>
  );
}

const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });

// Sales-type reports exclude documents imported from QuickBooks by default:
// their VAT was already declared there, so counting them again overstates
// turnover. This lets you flip to the combined view when you want it.
function HistoryToggle({ on, setOn }) {
  return (
    <label className="flex items-start gap-2 bg-gray-50 border rounded-lg px-3 py-2 cursor-pointer">
      <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)}
        className="mt-0.5 accent-orange-500" />
      <span className="text-xs text-gray-600">
        <strong>Include QuickBooks history</strong> — off by default. Invoices imported from
        QuickBooks are not sales made here and their VAT was already declared, so they are
        left out of these figures.
      </span>
    </label>
  );
}

// ── Sales report (invoiced sales by month + top clients) ──────────────────────
export function TabSales() {
  const { from, to, setFrom, setTo } = useRange(30);
  const [hist, setHist] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['sales-report', from, to, hist],
    queryFn: () => api.get('/billing/sales-report', { params: { from, to, include_history: hist || undefined } }).then(r => r.data),
    retry: false,
  });
  const t = data?.totals;

  const subtitle = `${from} to ${to} · invoice basis, net of credit notes${hist ? ' · incl. QuickBooks history' : ''}`;
  const summary = data && {
    filename: `sales-${from}-to-${to}`, title: 'Sales report', subtitle,
    sections: [
      { name: 'Month by month', columns: [
          { header: 'Month', value: (m) => m.month === 'Total' ? 'Total' : monthName(m.month), width: 18 },
          { header: 'Invoices', value: (m) => m.invoice_count, align: 'right', width: 10 },
          { header: 'Excl VAT', value: (m) => money(m.subtotal), align: 'right', width: 14 },
          { header: 'VAT', value: (m) => money(m.vat), align: 'right', width: 12 },
          { header: 'Incl VAT', value: (m) => money(m.total), align: 'right', width: 14 },
        ], rows: data.monthly, totals: { month: 'Total', ...t } },
      { name: 'Top clients by invoiced value', columns: [
          { header: 'Client', value: (c) => c.name, width: 34 },
          { header: 'Invoices', value: (c) => c.invoice_count, align: 'right', width: 10 },
          { header: 'Invoiced (incl VAT, net of credits)', value: (c) => money(c.total), align: 'right', width: 18 },
        ], rows: data.clients },
    ],
  };
  const detailed = async () => {
    const d = (await api.get('/billing/sales-report', { params: { from, to, include_history: hist || undefined, detail: 1 } })).data;
    const inv = d.detail?.invoices || [], cn = d.detail?.credit_notes || [];
    return { ...summary, sections: [
      ...summary.sections,
      { name: `Invoices issued (${inv.length})`, columns: INVOICE_LINES, rows: inv,
        totals: { invoice_number: 'Total', client: '', subtotal: sumOf(inv, 'subtotal'), vat_amount: sumOf(inv, 'vat_amount'), total: sumOf(inv, 'total'), status: '' } },
      { name: `Credit notes (${cn.length})`, columns: CREDIT_LINES, rows: cn,
        totals: { credit_note_number: 'Total', client: '', invoice_number: '', subtotal: sumOf(cn, 'subtotal'), vat_amount: sumOf(cn, 'vat_amount'), total: sumOf(cn, 'total') } },
    ] };
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>What you invoiced.</strong> Every tax invoice you issued in the period, less any credit
        notes, split into excl-VAT, VAT and incl-VAT. This is the sales figure your accountant reconciles
        against — an invoice counts on the day it was <strong>issued</strong>, not when it was paid.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <HistoryToggle on={hist} setOn={setHist} />
      {error ? <div className="bg-white rounded-xl shadow-sm p-6 text-center text-sm text-red-600">{error.response?.data?.error || 'Could not build the sales report'}</div>
      : isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      : data && (
        <>
          <div className="flex justify-end">
            <ExportMenu summary={summary} detailed={detailed} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Invoiced (incl VAT)" value={Rand(t.total)} colorClass="bg-green-50 text-green-700" />
            <StatCard label="Excl VAT (sales)" value={Rand(t.subtotal)} colorClass="bg-blue-50 text-blue-700" />
            <StatCard label="VAT" value={Rand(t.vat)} colorClass="bg-gray-50 text-gray-700" />
            <StatCard label="Invoices issued" value={t.invoice_count}
              sub={`Avg ${Rand(t.avg_invoice)} each${t.credit_note_count ? ` · ${t.credit_note_count} credit notes` : ''}`}
              colorClass="bg-orange-50 text-orange-700" />
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Month by month</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Month</th>
                    <th className="px-4 py-2 text-right">Invoices</th>
                    <th className="px-4 py-2 text-right">Excl VAT</th>
                    <th className="px-4 py-2 text-right">VAT</th>
                    <th className="px-4 py-2 text-right">Incl VAT</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.monthly.map((m) => (
                    <tr key={m.month} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-700">{monthName(m.month)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{m.invoice_count}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.subtotal)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.vat)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">{Rand(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data.clients.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Top clients by invoiced value</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left w-8">#</th>
                      <th className="px-4 py-2 text-left">Client</th>
                      <th className="px-4 py-2 text-right">Invoices</th>
                      <th className="px-4 py-2 text-right">Invoiced (incl VAT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.clients.map((c, i) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-400 font-semibold">{i + 1}</td>
                        <td className="px-4 py-2"><a href={`/clients/${c.id}`} className="font-medium text-gray-800 hover:text-orange-500">{c.name}</a></td>
                        <td className="px-4 py-2 text-right text-gray-500">{c.invoice_count}</td>
                        <td className="px-4 py-2 text-right font-semibold text-gray-800">{Rand(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Payments received (by method) ─────────────────────────────────────────────
export function TabPayments() {
  const { from, to, setFrom, setTo } = useRange(30);
  const [hist, setHist] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['payments-report', from, to, hist],
    queryFn: () => api.get('/billing/payments-report', { params: { from, to, include_history: hist || undefined } }).then(r => r.data),
    retry: false,
  });
  const t = data?.totals;

  const summary = data && {
    filename: `payments-${from}-to-${to}`, title: 'Payments received',
    subtitle: `${from} to ${to}${hist ? ' · incl. QuickBooks history' : ''}`,
    sections: [
      { name: 'Month by month', columns: [
          { header: 'Month', value: (m) => m.month === 'Total' ? 'Total' : monthName(m.month), width: 18 },
          { header: 'Card', value: (m) => money(m.card), align: 'right', width: 12 },
          { header: 'Cash', value: (m) => money(m.cash), align: 'right', width: 12 },
          { header: 'EFT (Capitec)', value: (m) => money(m.eft), align: 'right', width: 14 },
          { header: 'Total', value: (m) => money(m.total), align: 'right', width: 14 },
          { header: 'Receipts', value: (m) => m.count, align: 'right', width: 10 },
        ], rows: data.monthly, totals: { month: 'Total', ...t } },
    ],
  };
  const detailed = async () => {
    const d = (await api.get('/billing/payments-report', { params: { from, to, include_history: hist || undefined, detail: 1 } })).data;
    const pay = d.detail?.payments || [];
    return { ...summary, sections: [
      ...summary.sections,
      { name: `Every payment (${pay.length} rows; a receipt split over several invoices is one row per invoice)`, columns: [
          { header: 'Date', value: (r) => dateOnly(r.date), width: 12 },
          { header: 'Client', value: (r) => r.client, width: 32 },
          { header: 'Invoice', value: (r) => r.invoice_number, width: 14 },
          { header: 'Method', value: (r) => r.method, width: 8 },
          { header: 'Bank', value: (r) => r.bank_account || '', width: 10 },
          { header: 'Reference', value: (r) => r.reference || '', width: 18 },
          { header: 'Amount', value: (r) => money(r.amount), align: 'right', width: 12 },
          { header: 'Recorded by', value: (r) => r.recorded_by || '', width: 16 },
        ], rows: pay, totals: { date: 'Total', client: '', invoice_number: '', method: '', bank_account: '', reference: '', amount: sumOf(pay, 'amount'), recorded_by: '' } },
    ] };
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Money actually received.</strong> Every payment recorded in the period, split by how it came
        in — card machine, cash at the office, or EFT into Capitec. Use this to reconcile a whole month
        against the bank (the Cash-up page does the same for a single day).
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <HistoryToggle on={hist} setOn={setHist} />
      {error ? <div className="bg-white rounded-xl shadow-sm p-6 text-center text-sm text-red-600">{error.response?.data?.error || 'Could not build the payments report'}</div>
      : isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      : data && (
        <>
          <div className="flex justify-end">
            <ExportMenu summary={summary} detailed={detailed} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total received" value={Rand(t.total)} sub={`${t.count} payments`} colorClass="bg-green-50 text-green-700" />
            <StatCard label="Card" value={Rand(t.card)} colorClass="bg-blue-50 text-blue-700" />
            <StatCard label="Cash" value={Rand(t.cash)} colorClass="bg-amber-50 text-amber-700" />
            <StatCard label="EFT (Capitec)" value={Rand(t.eft)} colorClass="bg-indigo-50 text-indigo-700" />
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Month by month</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Month</th>
                    <th className="px-4 py-2 text-right">Card</th>
                    <th className="px-4 py-2 text-right">Cash</th>
                    <th className="px-4 py-2 text-right">EFT</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.monthly.map((m) => (
                    <tr key={m.month} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-700">{monthName(m.month)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.card)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.cash)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.eft)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">{Rand(m.total)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Income vs expenses (rough P&L) ────────────────────────────────────────────
export function TabPnl() {
  const { from, to, setFrom, setTo } = useRange(90);
  const [hist, setHist] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['pnl', from, to, hist],
    queryFn: () => api.get('/billing/pnl', { params: { from, to, include_history: hist || undefined } }).then(r => r.data),
    retry: false,
  });

  const summary = data && {
    filename: `income-vs-expenses-${from}-to-${to}`, title: 'Income vs expenses (rough profit guide)',
    subtitle: `${from} to ${to} · revenue = invoiced excl VAT net of credit notes; expenses = fuel + petty cash${hist ? ' · incl. QuickBooks history' : ''}`,
    sections: [
      { name: 'Month by month', columns: [
          { header: 'Month', value: (m) => m.month === 'Total' ? 'Total' : monthName(m.month), width: 18 },
          { header: 'Revenue (excl VAT)', value: (m) => money(m.revenue), align: 'right', width: 16 },
          { header: 'Fuel', value: (m) => money(m.fuel), align: 'right', width: 12 },
          { header: 'Petty cash', value: (m) => money(m.petty_cash), align: 'right', width: 12 },
          { header: 'Rough profit', value: (m) => money(m.profit), align: 'right', width: 14 },
        ], rows: data.monthly, totals: { month: 'Total', revenue: data.revenue, fuel: data.fuel, petty_cash: data.petty_cash, profit: data.profit } },
    ],
  };
  const detailed = async () => {
    const d = (await api.get('/billing/pnl', { params: { from, to, include_history: hist || undefined, detail: 1 } })).data;
    const inv = d.detail?.invoices || [], cn = d.detail?.credit_notes || [], fuel = d.detail?.fuel || [], petty = d.detail?.petty_cash || [];
    return { ...summary, sections: [
      ...summary.sections,
      { name: `Invoices (${inv.length})`, columns: INVOICE_LINES, rows: inv,
        totals: { invoice_number: 'Total', client: '', subtotal: sumOf(inv, 'subtotal'), vat_amount: sumOf(inv, 'vat_amount'), total: sumOf(inv, 'total'), status: '' } },
      { name: `Credit notes (${cn.length})`, columns: CREDIT_LINES, rows: cn,
        totals: { credit_note_number: 'Total', client: '', invoice_number: '', subtotal: sumOf(cn, 'subtotal'), vat_amount: sumOf(cn, 'vat_amount'), total: sumOf(cn, 'total') } },
      { name: `Fuel (${fuel.length}; entries held for review are listed but not counted)`, columns: [
          { header: 'Date', value: (r) => dateOnly(r.date), width: 12 },
          { header: 'Vehicle', value: (r) => r.vehicle || '', width: 16 },
          { header: 'Litres', value: (r) => r.liters == null ? '' : parseFloat(r.liters).toFixed(2), align: 'right', width: 10 },
          { header: 'Cost', value: (r) => money(r.total_cost), align: 'right', width: 12 },
          { header: 'Odometer', value: (r) => r.odometer_km || '', align: 'right', width: 10 },
          { header: 'Held for review', value: (r) => (r.needs_review ? 'yes' : ''), width: 10 },
          { header: 'Notes', value: (r) => r.notes || '', width: 30 },
        ], rows: fuel, totals: { date: 'Total (counted)', vehicle: '', liters: null, total_cost: fuel.filter((r) => !r.needs_review).reduce((s, r) => s + parseFloat(r.total_cost || 0), 0), odometer_km: '', needs_review: false, notes: '' } },
      { name: `Petty cash (${petty.length})`, columns: [
          { header: 'Date', value: (r) => dateOnly(r.date), width: 12 },
          { header: 'What for', value: (r) => r.description || '', width: 40 },
          { header: 'Amount', value: (r) => money(r.amount), align: 'right', width: 12 },
          { header: 'Recorded by', value: (r) => r.recorded_by || '', width: 16 },
        ], rows: petty, totals: { date: 'Total', description: '', amount: sumOf(petty, 'amount'), recorded_by: '' } },
    ] };
  };

  return (
    <div className="space-y-4">
      <Explain>
        <strong>A rough profit guide — not a full P&amp;L.</strong> Revenue is what you invoiced (excl VAT,
        net of credit notes). Expenses cover <strong>fuel</strong> and <strong>petty cash</strong> — salaries,
        rent, vehicle maintenance and anything else paid from the bank are <strong>not</strong> included, so
        treat this as a guide, not your accountant's profit figure.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <HistoryToggle on={hist} setOn={setHist} />
      {error ? <div className="bg-white rounded-xl shadow-sm p-6 text-center text-sm text-red-600">{error.response?.data?.error || 'Could not build this report'}</div>
      : isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      : data && (
        <>
          <div className="flex justify-end">
            <ExportMenu summary={summary} detailed={detailed} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Revenue (excl VAT)" value={Rand(data.revenue)} colorClass="bg-green-50 text-green-700" />
            <StatCard label="Fuel" value={Rand(data.fuel)}
              sub={data.fuel_flagged?.count ? `${data.fuel_flagged.count} entries (${Rand(data.fuel_flagged.total)}) held for review` : undefined}
              colorClass="bg-red-50 text-red-700" />
            <StatCard label="Petty cash spent" value={Rand(data.petty_cash)} colorClass="bg-red-50 text-red-700" />
            <StatCard label="Rough profit" value={Rand(data.profit)}
              colorClass={data.profit >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'} />
          </div>
          {data.other_branch_fuel?.total > 0 && (
            <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2">
              A further <strong>{Rand(data.other_branch_fuel.total)}</strong> of fuel
              ({data.other_branch_fuel.count} fill-ups) belongs to the <strong>Fastway branch</strong> and is
              deliberately left out — that branch earns its own income, which this system doesn&apos;t see, so
              charging its fuel against courier revenue would show a loss that isn&apos;t real.
              See <a href="/fuel" className="text-orange-500 hover:underline">Fuel</a>.
            </p>
          )}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Month by month</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Month</th>
                    <th className="px-4 py-2 text-right">Revenue (excl VAT)</th>
                    <th className="px-4 py-2 text-right">Fuel</th>
                    <th className="px-4 py-2 text-right">Petty cash</th>
                    <th className="px-4 py-2 text-right">Rough profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.monthly.map((m) => (
                    <tr key={m.month} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-700">{monthName(m.month)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{Rand(m.revenue)}</td>
                      <td className="px-4 py-2 text-right text-red-600">{m.fuel > 0 ? `− ${Rand(m.fuel)}` : Rand(0)}</td>
                      <td className="px-4 py-2 text-right text-red-600">{m.petty_cash > 0 ? `− ${Rand(m.petty_cash)}` : Rand(0)}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${m.profit >= 0 ? 'text-gray-800' : 'text-red-600'}`}>{Rand(m.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Uninvoiced value (work in progress) ───────────────────────────────────────
export function TabWip() {
  const { data, isLoading } = useQuery({
    queryKey: ['ready-to-invoice'],
    queryFn: () => api.get('/billing/ready-to-invoice').then(r => r.data),
  });

  const summary = data && {
    filename: 'uninvoiced-value', title: 'Uninvoiced value', subtitle: 'Delivered, priced, not yet on an invoice',
    sections: [{ name: 'By client', columns: [
        { header: 'Client', value: (c) => c.client_name, width: 34 },
        { header: 'Parcels', value: (c) => (c.parcels ? c.parcels.length : c.parcel_count), align: 'right', width: 10 },
        { header: 'Value (incl VAT)', value: (c) => money(c.total), align: 'right', width: 16 },
      ], rows: data.clients, totals: { client_name: 'Total', parcel_count: data.parcel_count, total: data.total } }],
  };
  const detailed = () => ({ ...summary, sections: [
    ...summary.sections,
    { name: 'Every parcel waiting', columns: [
        { header: 'Client', value: (p) => p.client_name, width: 30 },
        { header: 'Tracking', value: (p) => p.tracking_number, width: 14 },
        { header: 'Delivered', value: (p) => dateOnly(p.delivered_at || p.updated_at), width: 12 },
        { header: 'Recipient', value: (p) => p.recipient_name || '', width: 26 },
        { header: 'Price (incl VAT)', value: (p) => money(p.price), align: 'right', width: 14 },
      ], rows: data.clients.flatMap((c) => (c.parcels || []).map((p) => ({ ...p, client_name: c.client_name }))) },
  ] });

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Money you've earned but haven't billed yet.</strong> Parcels that are delivered and priced
        but not on an invoice. This is revenue sitting in the pipeline — turn it into invoices from the
        <strong> Billing → Ready to Invoice</strong> tab.
      </Explain>
      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      : data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Uninvoiced value (incl VAT)" value={Rand(data.total)}
              colorClass={data.total > 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'} />
            <StatCard label="Parcels waiting" value={data.parcel_count}
              sub={`${data.clients.length} clients`} colorClass="bg-gray-50 text-gray-700" />
          </div>
          <div className="flex justify-end">
            <ExportMenu summary={summary} detailed={detailed} disabled={!data.clients.length} />
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {data.clients.length === 0 ? (
              <div className="p-8 text-center text-gray-400">Nothing waiting — every delivered, priced parcel is invoiced ✓</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Client</th>
                      <th className="px-4 py-3 text-right">Parcels</th>
                      <th className="px-4 py-3 text-right">Value (incl VAT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.clients.map((c) => (
                      <tr key={c.client_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3"><a href={`/clients/${c.client_id}`} className="font-medium text-gray-800 hover:text-orange-500">{c.client_name}</a></td>
                        <td className="px-4 py-3 text-right text-gray-500">{c.parcels.length}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">{Rand(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TabTopClients() {
  const { from, to, setFrom, setTo } = useRange(30);
  const { data, isLoading } = useQuery({
    queryKey: ['top-clients', from, to],
    queryFn: () => api.get('/reports/top-clients', { params: { from, to } }).then(r => r.data),
  });
  const clients = data?.clients || [];
  const maxRev = Math.max(...clients.map(c => parseFloat(c.revenue)), 1);

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Who sends you the most work and money.</strong> Clients are ranked by the revenue of parcels
        they sent in the period. Use this to see which clients your business depends on, and who deserves a
        call if their numbers drop.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
        : clients.length === 0 ? <div className="p-8 text-center text-gray-400">No parcels in this period</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left w-8">#</th>
                  <th className="px-4 py-3 text-left">Client (sender)</th>
                  <th className="px-4 py-3 text-right">Parcels</th>
                  <th className="px-4 py-3 text-right">Delivered</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-left w-1/3">Share of revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clients.map((c, i) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-400 font-semibold">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{c.parcels}</td>
                    <td className="px-4 py-3 text-right text-green-600">{c.delivered}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{Rand(c.revenue)}</td>
                    <td className="px-4 py-3">
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full"
                          style={{ width: `${(parseFloat(c.revenue) / maxRev) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 4. Failed deliveries ──────────────────────────────────────────────────────
function TabFailures() {
  const { from, to, setFrom, setTo } = useRange(30);
  const { data, isLoading } = useQuery({
    queryKey: ['failures', from, to],
    queryFn: () => api.get('/reports/failures', { params: { from, to } }).then(r => r.data),
  });
  const total = (data?.byReason || []).reduce((s, r) => s + parseInt(r.count), 0);

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Why deliveries fail, and who they fail with.</strong> Every failed delivery attempt in the
        period, grouped by reason and by driver. Lots of "Out of time" = routes are overloaded. Lots of
        "Wrong address" = check the address details when parcels are booked.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div> : data && (
        <>
          <StatCard label="Failed delivery attempts in this period" value={total}
            colorClass={total > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="font-semibold text-gray-700 mb-3">By reason</h2>
              {data.byReason.length === 0 ? <p className="text-sm text-gray-400">No failures 🎉</p> :
                data.byReason.map(r => (
                  <div key={r.reason} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{FAILURE_LABELS[r.reason] || r.reason}</span>
                      <span className="font-semibold text-gray-800">{r.count} ({Math.round(r.count / total * 100)}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full" style={{ width: `${(r.count / total) * 100}%` }} />
                    </div>
                  </div>
                ))}
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="font-semibold text-gray-700 mb-3">By driver</h2>
              {data.byDriver.length === 0 ? <p className="text-sm text-gray-400">No failures 🎉</p> :
                data.byDriver.map(d => (
                  <div key={d.driver_name} className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-gray-700">{d.driver_name}</span>
                    <span className="font-semibold text-red-600">{d.count}</span>
                  </div>
                ))}
            </div>
          </div>
          {data.recent.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <h2 className="font-semibold text-gray-700 px-5 pt-4 pb-2">Most recent failures</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Tracking</th>
                      <th className="px-4 py-2 text-left">Recipient</th>
                      <th className="px-4 py-2 text-left">Driver</th>
                      <th className="px-4 py-2 text-left">Reason</th>
                      <th className="px-4 py-2 text-left">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.recent.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <a href={`/parcels/${r.tracking_number}`} className="font-mono text-xs font-semibold text-orange-500 hover:underline">{r.tracking_number}</a>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs">{r.recipient_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs">{r.driver_name || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-red-600">
                          {FAILURE_LABELS[r.failure_reason] || r.failure_reason || '—'}
                          {r.failure_notes ? <span className="text-gray-400"> — {r.failure_notes}</span> : null}
                        </td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">
                          {new Date(r.scanned_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── 5. Delivery speed (turnaround) ───────────────────────────────────────────
function fmtHours(h) {
  if (h == null) return '—';
  if (h < 24) return `${h.toFixed(1)} hours`;
  return `${(h / 24).toFixed(1)} days`;
}

function TabTurnaround() {
  const { from, to, setFrom, setTo } = useRange(30);
  const { data, isLoading } = useQuery({
    queryKey: ['turnaround', from, to],
    queryFn: () => api.get('/reports/turnaround', { params: { from, to } }).then(r => r.data),
  });

  const del = data?.delivered || [];
  const hoursArr = del.map(d => parseFloat(d.hours));
  const avgH = hoursArr.length ? hoursArr.reduce((a, b) => a + b, 0) / hoursArr.length : null;
  const buckets = [
    { label: 'Same day (under 24h)', test: h => h < 24 },
    { label: '1–2 days',             test: h => h >= 24 && h < 48 },
    { label: '2–3 days',             test: h => h >= 48 && h < 72 },
    { label: 'Over 3 days',          test: h => h >= 72 },
  ].map(b => ({ ...b, count: hoursArr.filter(b.test).length }));

  return (
    <div className="space-y-4">
      <Explain>
        <strong>How fast parcels get delivered.</strong> For every parcel delivered in the period, we measure
        the time from when it was created to when it was delivered. The "still busy" list shows the oldest
        parcels that are not delivered yet — those may need a phone call.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div> : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Parcels delivered" value={del.length} colorClass="bg-green-50 text-green-700" />
            <StatCard label="Average time to deliver" value={fmtHours(avgH)} colorClass="bg-blue-50 text-blue-700" />
            <StatCard label="Slowest delivery" value={fmtHours(hoursArr.length ? Math.max(...hoursArr) : null)} colorClass="bg-amber-50 text-amber-700" />
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="font-semibold text-gray-700 mb-3">How long deliveries took</h2>
            {del.length === 0 ? <p className="text-sm text-gray-400">No deliveries in this period</p> :
              buckets.map(b => (
                <div key={b.label} className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{b.label}</span>
                    <span className="font-semibold text-gray-800">{b.count} ({Math.round(b.count / del.length * 100)}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(b.count / del.length) * 100}%` }} />
                  </div>
                </div>
              ))}
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="font-semibold text-gray-700 px-5 pt-4 pb-2">Oldest parcels still busy (not delivered yet)</h2>
            {data.slowestOpen.length === 0 ? <div className="p-6 text-center text-green-600 text-sm">✅ Nothing outstanding</div> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Tracking</th>
                    <th className="px-4 py-2 text-left">Recipient</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Days old</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.slowestOpen.map(p => (
                    <tr key={p.tracking_number} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <a href={`/parcels/${p.tracking_number}`} className="font-mono text-xs font-semibold text-orange-500 hover:underline">{p.tracking_number}</a>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{p.recipient_name || '—'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs font-semibold text-amber-600">{p.age_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── 6. Stuck parcels ──────────────────────────────────────────────────────────
function TabStuck() {
  const [days, setDays] = useState(3);
  const { data, isLoading } = useQuery({
    queryKey: ['stuck', days],
    queryFn: () => api.get('/reports/stuck', { params: { days } }).then(r => r.data),
  });
  const rows = data?.parcels || [];

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Parcels that stopped moving.</strong> These parcels are not delivered or cancelled, and
        nothing has happened to them for the number of days you choose below. Each one probably needs
        action — a phone call, a reschedule, or a scan that was forgotten.
      </Explain>
      <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-600 font-medium">Show parcels with no movement for:</span>
        {[2, 3, 5, 7, 14].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              days === d ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-600 hover:border-orange-400'
            }`}>
            {d}+ days
          </button>
        ))}
      </div>
      <StatCard label={`Parcels stuck for ${days}+ days`} value={rows.length}
        colorClass={rows.length > 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'} />
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div>
        : rows.length === 0 ? <div className="p-8 text-center text-green-600 font-medium">✅ No stuck parcels — everything is moving</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Tracking</th>
                  <th className="px-4 py-3 text-left">Recipient</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Driver</th>
                  <th className="px-4 py-3 text-left">Last movement</th>
                  <th className="px-4 py-3 text-right">Days stuck</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(p => (
                  <tr key={p.tracking_number} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <a href={`/parcels/${p.tracking_number}`} className="font-mono text-xs font-semibold text-orange-500 hover:underline">{p.tracking_number}</a>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {p.recipient_name || '—'}
                      {p.recipient_phone && <span className="text-gray-400"> · {p.recipient_phone}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{p.driver_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{dmy(p.updated_at)}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">{p.days_stuck}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 7. Driver daily summaries (admin) ─────────────────────────────────────────
function TabDriverDays() {
  const { from, to, setFrom, setTo } = useRange(7);
  const { data, isLoading } = useQuery({
    queryKey: ['driver-days', from, to],
    queryFn: () => api.get('/reports/driver-days', { params: { from, to } }).then(r => r.data),
  });
  const rows = data?.days || [];

  // Group by day for readable sections
  const byDay = rows.reduce((acc, r) => {
    (acc[r.day] = acc[r.day] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <Explain>
        <strong>Each driver's working day, side by side.</strong> For every day a driver made delivery
        attempts: how many stops, how many delivered vs failed, when they started (first stop), when they
        finished (last stop), and their pace in stops per hour. The same numbers drivers see on their app's
        "Today" tab.
      </Explain>
      <RangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div>
      : rows.length === 0 ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">No driver activity in this period</div>
      : Object.entries(byDay).map(([day, dayRows]) => (
        <div key={day} className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-gray-700 px-5 pt-4 pb-2">
            {new Date(day).toLocaleDateString('en-ZA', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Driver</th>
                  <th className="px-4 py-2 text-right">Stops</th>
                  <th className="px-4 py-2 text-right">Delivered</th>
                  <th className="px-4 py-2 text-right">Failed</th>
                  <th className="px-4 py-2 text-right">First stop</th>
                  <th className="px-4 py-2 text-right">Last stop</th>
                  <th className="px-4 py-2 text-right">Pace</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dayRows.map(r => (
                  <tr key={r.driver_id + day} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800">{r.driver_name}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700 font-semibold">{r.stops}</td>
                    <td className="px-4 py-2.5 text-right text-green-600">{r.delivered}</td>
                    <td className="px-4 py-2.5 text-right text-red-600">{r.failed}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 text-xs">{hm(r.first_stop)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 text-xs">{hm(r.last_stop)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700 text-xs">
                      {r.stops_per_hour != null ? `${r.stops_per_hour} stops/hr` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Reports page ─────────────────────────────────────────────────────────
// Reports are grouped into sections; each section has one or more reports.
const SECTIONS = [
  {
    key: 'overview', label: 'Overview', icon: BarChart2,
    tabs: [{ key: 'daily', label: 'Daily Volume', component: TabOverview }],
  },
  {
    key: 'money', label: 'Money', icon: DollarSign,
    tabs: [
      { key: 'revenue',    label: 'Revenue',     component: TabRevenue },
      { key: 'sales',      label: 'Sales',       component: TabSales },
      { key: 'payments',   label: 'Payments',    component: TabPayments },
      { key: 'vat',        label: 'VAT (VAT201)', component: TabVat },
      { key: 'pnl',        label: 'Income vs Expenses', component: TabPnl },
      { key: 'wip',        label: 'Uninvoiced',  component: TabWip },
      { key: 'topclients', label: 'Top Clients', component: TabTopClients },
    ],
  },
  {
    key: 'ops', label: 'Operations', icon: Activity,
    tabs: [
      { key: 'failures',   label: 'Failed Deliveries', component: TabFailures },
      { key: 'turnaround', label: 'Delivery Speed',    component: TabTurnaround },
      { key: 'stuck',      label: 'Stuck Parcels',     component: TabStuck },
      { key: 'cancelled',  label: 'Cancelled',         component: TabCancelled },
    ],
  },
  {
    key: 'drivers', label: 'Drivers', icon: Truck,
    tabs: [
      { key: 'performance', label: 'Performance',     component: TabDrivers },
      { key: 'days',        label: 'Daily Summaries', component: TabDriverDays },
    ],
  },
];

export default function Reports() {
  const [sectionKey, setSectionKey] = useState('overview');
  const [tabKey,     setTabKey]     = useState('daily');

  const section   = SECTIONS.find(s => s.key === sectionKey) || SECTIONS[0];
  const activeTab = section.tabs.find(t => t.key === tabKey) || section.tabs[0];
  const Active    = activeTab.component;

  const pickSection = (s) => {
    setSectionKey(s.key);
    setTabKey(s.tabs[0].key);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-800">Reports</h1>

      {/* Sections */}
      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1.5 flex-wrap">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => pickSection(s)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors min-w-28 ${
              sectionKey === s.key ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <s.icon size={15} />
            {s.label}
          </button>
        ))}
      </div>

      {/* Reports inside the section */}
      {section.tabs.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {section.tabs.map(t => (
            <button key={t.key} onClick={() => setTabKey(t.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                activeTab.key === t.key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-orange-400'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <Active />
    </div>
  );
}
